/**
 * useEmailHygieneReconciliationQueue
 *
 * DASH-EMAIL-013: real-data hook for the active Email Hygiene
 * Reconciliation queue (GET /api/email-hygiene/reconciliations).
 *
 * Mirrors this codebase's existing loading/error/data/refresh hook shape
 * (useEmailDeliverabilityData, useEmailCreativeAnalyticsData) — initializes
 * `loading: true` so the queue renders its loading state on first paint.
 *
 * Only the filters the real RECON-002 API actually supports are accepted:
 * environment, reason, recipientEmail, limit, offset. Pagination follows
 * the API's own real total/limit/offset contract — `total` here is always
 * the backend's own count, never a frontend-computed length.
 *
 * RECONCILIATION-DEBT-012A: `total` is the TRUE population and is no longer
 * the same thing as "how many rows you can page through". Backend discovery is
 * a bounded window per class, so `reachableTotal` is what pagination must use;
 * paging over `total` would walk off the end of the window and read the empty
 * pages as the end of the queue. `discoveryTruncated` says the two differ.
 */
import { useState, useEffect, useCallback } from "react";
import {
  listEmailHygieneReconciliations,
  extractApiErrorMessage,
} from "../lib/emailHygieneReconciliationApi";

export function useEmailHygieneReconciliationQueue({
  environment = "all",
  reason = "all",
  recipientEmail = "",
  recoveryState = "all",
  limit = 25,
  offset = 0,
} = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listEmailHygieneReconciliations({
        environment, reason, recipientEmail, recoveryState, limit, offset,
      });
      setData(result);
    } catch (err) {
      setError(extractApiErrorMessage(err, "Failed to load Email Hygiene Reconciliations"));
    } finally {
      setLoading(false);
    }
  }, [environment, reason, recipientEmail, recoveryState, limit, offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    items: data?.items || [],
    total: data?.total ?? 0,
    nativeTotal: data?.native_total ?? 0,
    propagationTotal: data?.propagation_total ?? 0,
    // Paginate over this, never over `total`.
    reachableTotal: data?.reachable_total ?? data?.total ?? 0,
    discoveryTruncated: data?.discovery_truncated ?? false,
    discoveryCap: data?.discovery_cap ?? 0,
    countsExact: data?.counts_exact ?? true,
    limit: data?.limit ?? limit,
    offset: data?.offset ?? offset,
    refresh: fetchData,
  };
}

export default useEmailHygieneReconciliationQueue;
